import React, { useEffect, useState } from 'react';
import Navbar from '../../home/Navbar';
import Queryform from '../../pages/Queryform';
import Footer from '../../home/Footer';
import Loader from '../Loader';
import { API_URL, API_END_POINT } from '../Utils/Constant';
import { useNavigate } from 'react-router-dom';

const BlogHome = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch(`${API_END_POINT}astroblog/`);
        if (!response.ok) {
          throw new Error('Failed to fetch blogs');
        }
        const data = await response.json();
        setBlogs(data);
        setFilteredBlogs(data);

        // Extract unique categories
        const uniqueCategories = [...new Set(data.map(blog => blog.category))];
        setCategories(uniqueCategories);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    filterBlogs(category, searchQuery);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterBlogs(selectedCategory, query);
  };

  const filterBlogs = (category, query) => {
    let filtered = [...blogs];

    // Apply category filter
    if (category !== 'all') {
      filtered = filtered.filter(blog => blog.category === category);
    }

    // Apply search filter
    if (query.trim()) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(query.toLowerCase())
      );
    }

    setFilteredBlogs(filtered);
  };

  const handleImageError = (blogId) => {
    setImageLoadErrors(prev => ({
      ...prev,
      [blogId]: true
    }));
  };

  const handleBlogClick = (blogId, blogTitle) => {
    const encodedTitle = encodeURIComponent(blogTitle.toLowerCase().replace(/\s+/g, '-')); // Convert title to URL-friendly format
    navigate(`/blog/${encodedTitle}?id=${blogId}`); // Pass the ID as a query parameter
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-500 text-xl">Error: {error}</div>
      </div>
    );
  }

  return (
    <>
      <section className="bg-gradient-to-r from-white to-success/40 text-gray-900 min-h-screen">
        <div>
          <Navbar />
        </div>
        <div className="w-full">
          <div className="text-gray-900 flex flex-col items-center py-8 mx-auto">
            <h1 className="text-4xl text-secondary font-serif font-bold mb-2">Astro Blog</h1>
            <p className="text-lg mb-8 text-secondary font-serif text-center max-w-2xl">
              Read our latest blog posts and stay updated with the latest news and trends in the astrology world.
            </p>


            {/* Main Content with Sidebar */}
            <div className="flex flex-col md:flex-row w-full lg:w-10/12 md:w-10/12 mx-auto">
              {/* Sidebar for Categories */}
              <div className="w-full md:w-1/4 h-max  md:sticky md:top-11 lg:bg-white md:bg-white md:shadow-md lg:shadow-md rounded-lg p-4 mb-2 md:mb-0">
                <h3 className="text-lg font-semibold text-center text-white hidden md:block bg-secondary p-2 rounded-md mb-4">Categories</h3>
                <div className="hidden md:flex flex-col gap-2">
                  <button
                    onClick={() => handleCategorySelect('all')}
                    className={`px-4 py-2 rounded-lg transition-colors text-left ${selectedCategory === 'all'
                      ? 'bg-secondary text-white shadow-md'
                      : 'bg-gray-100 text-secondary hover:bg-gray-200'
                      }`}
                  >
                    All Posts
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => handleCategorySelect(category)}
                      className={`px-4 py-2 rounded-lg transition-colors text-left ${selectedCategory === category
                        ? 'bg-secondary text-white shadow-md'
                        : 'bg-gray-100 text-secondary hover:bg-gray-200'
                        }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>

                {/* Tabs for Small Devices */}
                <div className="flex md:hidden overflow-x-auto gap-2">
                  <div className="flex overflow-x-auto scrollbar-hide gap-2 px-4 py-1">
                    <button
                      onClick={() => handleCategorySelect('all')}
                      className={`px-6 py-2 rounded-full transition-colors whitespace-nowrap ${selectedCategory === 'all'
                        ? 'bg-secondary text-white shadow-md'
                        : 'bg-white text-secondary hover:bg-gray-100'
                        }`}
                    >
                      All Posts
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className={`px-6 py-2 rounded-full transition-colors whitespace-nowrap ${selectedCategory === category
                          ? 'bg-secondary text-white shadow-md'
                          : 'bg-white text-secondary hover:bg-gray-100'
                          }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Blog Grid */}
              <div className="w-full">

                {/* Search Bar */}
                <div className="w-[95%] mx-auto mb-4 px-4">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search blogs by title..."
                      value={searchQuery}
                      onChange={handleSearch}
                      className="w-full px-6 py-3 rounded-full border-2 border-gray-200 focus:border-secondary focus:outline-none shadow-sm transition-all duration-300"
                    />
                    <svg
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 px-4">
                  {filteredBlogs.length === 0 ? (
                    <div className="col-span-full text-center py-12">
                      <p className="text-gray-500 text-lg">No blogs found matching your search criteria.</p>
                    </div>
                  ) : (
                    filteredBlogs.map((blog) => (
                      <div
                        key={blog._id}
                        className="bg-white shadow-lg rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                      >
                        {!imageLoadErrors[blog._id] ? (
                          <img
                            src={blog.image}
                            alt={blog.title}
                            className="w-full h-48 object-fit"
                            onError={() => handleImageError(blog._id)}
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                            <span className="text-gray-500">No image available</span>
                          </div>
                        )}
                        <div className="p-4">
                          <div className="mt-2 mb-2">
                            <span className="bg-secondary text-white px-2 py-1 text-sm rounded-full">
                              {blog.category}
                            </span>
                          </div>
                          <h2
                            className="text-xl font-semibold text-gray-800 cursor-pointer hover:text-secondary transition-colors"
                            onClick={() => handleBlogClick(blog._id, blog.title)}
                          >
                            {blog.title}
                          </h2>
                          <p className="text-gray-600 mt-2">
                            {blog.content.slice(0, 100)}
                            <span
                              className='text-blue-500 cursor-pointer hover:text-blue-700'
                              onClick={() => handleBlogClick(blog._id, blog.title)}
                            >
                              Read more...
                            </span>
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <Queryform />
        <Footer />
      </section>
    </>
  );
};

export default BlogHome;
